import React, { useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, Label, FormGroup } from 'reactstrap';
import styled from 'styled-components';
import { FormProvider, useForm } from 'react-hook-form';
import useUser from '../../../customHooks/useUser';
import { Button } from 'reactstrap';
import { zodResolver } from "@hookform/resolvers/zod";
import { ModalFooter } from 'reactstrap';
import moment from 'moment';
import { ButtonGroup } from '@material-ui/core';
import { DelayCode } from '../../../globals/interfaces';
import { AircraftTypeSchema, AircraftTypeSchemaUpdate } from './validation';
import { createdUpdatedInfo, getTsDate } from '../../../utils';
import BackButton from '../../../components/custom/BackButton';
import Input from '../../../components/Input';
import { ZodFormProvider, useZodForm } from '../../../components/custom/ZodForm/ZodFormProvider';
import { createZodInput } from '../../../components/custom/ZodForm/TypedInput';
import ActionIcon from '../../../components/custom/ActionIcon';

const FormInput = createZodInput<DelayCode>();

interface Props {
    modal: boolean;
    setModal: (state: boolean) => void;
    record?: DelayCode;
    onSubmit: (data: DelayCode, update?: boolean) => void;
    deleteItem: (id: number) => Promise<void>;
}

export default function DelayCodesModal({
    modal,
    setModal,
    record,
    onSubmit,
    deleteItem
}: Props) {

    const { user } = useUser();
    const update = Boolean(record);

    // Create form with strict validation options
    const methods = useZodForm<DelayCode>({
        resolver: update ? zodResolver(AircraftTypeSchemaUpdate) : zodResolver(AircraftTypeSchema),
        mode: "onChange",
        reValidateMode: "onChange",
        shouldFocusError: true,
        // Ensure number fields are properly converted
        context: { valueAsNumber: true },
    });

    const {
        handleSubmit,
        reset,
        clearErrors,
        trigger,
        formState: { errors, isValid, isDirty },
        watch,
        setValue,
        requiredFields
    } = methods;

    // Reset form when modal opens/closes or record changes
    useEffect(() => {
        if (modal) {
            // Clear all previous errors
            clearErrors();
            const now = getTsDate();

            if (record) {
                reset({
                    ...record,
                    modified: now,
                    modifiedBy: user.s_email
                });
            } else {
                reset({
                    category: '',
                    number: null,
                    code: '',
                    title: '',
                    description: '',       
                    created: now,
                    createdBy: user.s_email,
                    modified: now,
                    modifiedBy: user.s_email
                });
            }

            trigger();
        }
    }, [modal, record, reset, clearErrors, user.s_email]);

    const toggle = () => setModal(!modal);

    return (
        <CustomModal isOpen={modal} toggle={toggle}>
            <ModalHeader className={'d-flex'}>
                <HeaderContainer>
                    <div>
                        <BackButton onClick={toggle} />
                        <h4 className={'pl-2'}>{update ? 'Update' : 'Create'} Delay Code</h4>
                    </div>
                </HeaderContainer>
            </ModalHeader>
            <ModalBody>
                <ZodFormProvider methods={methods} requiredFields={requiredFields}>
                    <FlexContainer>
                        <div>
                            <FormInput label="Category" name={'category'} />
                            <FormInput label="Number" name={'number'} type={'number'} />
                            <FormInput label="Code" name={'code'} />
                            <FormInput label="Title" name={'title'} />
                            <FormInput label="Description" name={'description'} />
                        </div>
                    </FlexContainer>
                </ZodFormProvider>
            </ModalBody>
            <ExpandedFooter>
                <FooterContentContainer>
                    <Label className={'d-block'}>
                        {createdUpdatedInfo(record, true)}
                    </Label>
                    <FooterButtonsContainer update={update}>
                        {
                            update && (
                                <ActionIcon
                                    type={'delete'}
                                    onClick={() => deleteItem(record.id)}
                                    simulateLoading
                                />
                            )
                        }
                        <ActionIcon
                            type={'save'}
                            disabled={!isDirty || !isValid}
                            onClick={handleSubmit(data => onSubmit(data, update))}
                            simulateLoading
                        />
                    </FooterButtonsContainer>
                </FooterContentContainer>
            </ExpandedFooter>
        </CustomModal>
    );
}

const HeaderContainer = styled.div`
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;

    & div {
        display: flex;
        align-items: center;
    }
`;

const FlexContainer = styled.div`
    display: flex;
    gap: 25px;
    width: 100%;

    & div {
        width: 100%;
    }
`;

const FooterFlex = styled.div`
    display: flex;
`;

const CustomModal = styled(Modal)`
    width: 100%;
    max-width: 1000px;
`;

const ExpandedFooter = styled(ModalFooter)`
    width: 100%;
`;

const FooterContentContainer = styled.div`
    width: 100%;
`;

const FooterButtonsContainer = styled.div<{ update?: boolean }>`
    width: 100%;
    display: flex;
    justify-content: ${p => p.update ? 'space-between' : 'flex-end'};
`;